import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Property } from '../models/property';
import { 
  FileText, 
  Hammer, 
  Lightbulb, 
  Home, 
  BarChart3, 
  FileCheck,
  ArrowLeft,
  PlusCircle
} from 'lucide-react';

interface SideNavigationProps {
  isOpen: boolean;
  toggleDrawer: () => void;
  currentProperty?: Property | null;
}

// Modern Tailwind-based sidebar component that's aware of the current property
const SideNavigation: React.FC<SideNavigationProps> = ({ 
  isOpen, 
  toggleDrawer,
  currentProperty 
}) => {
  const location = useLocation();
  const { propertyId: paramsPropertyId } = useParams<{ propertyId: string }>();
  
  // Extract property ID directly from the URL path as a fallback
  const pathParts = location.pathname.split('/');
  const urlPropertyId = location.pathname.startsWith('/property/') ? 
    pathParts[2] : // this gets the ID from /property/{id}/*
    null;
  
  // Use the ID from URL params if available, otherwise fall back to path extraction
  const propertyId = paramsPropertyId || urlPropertyId;
  
  const isPropertyPage = location.pathname.includes('/property/') && propertyId;
  
  const isActive = (path: string) => {
    return location.pathname.includes(path);
  };
  
  // Extract the activeTab from the URL if available
  let activeTab = '';
  if (isPropertyPage && location.search) {
    const params = new URLSearchParams(location.search);
    activeTab = params.get('tab') || '';
  }
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed left-0 top-16 bottom-0 w-64 bg-fxer-blue z-40 overflow-auto">
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="space-y-2">
            {/* All Properties Link */}
            <li>
              <Link 
                to="/property" 
                className="flex items-center p-2 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                <Home className="w-5 h-5" />
                <span className="ml-3">All Properties</span>
              </Link>
            </li>
            
            {/* Property Context Menu - Only shown when on a property page */}
            {isPropertyPage ? (
              currentProperty ? (
                <div className="pt-4 mt-4">
                  {/* Property title as a collapsible section header */}
                  <Link 
                    to={`/property/${propertyId}`}
                    className="p-2 flex items-center bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-lg mb-1"
                  >
                    <Home className="w-5 h-5 mr-2" />
                    <span className="truncate">
                      {currentProperty.address || 'Property'}
                      {!currentProperty.address && <span className="text-xs ml-2 text-gray-400">(ID: {propertyId})</span>}
                    </span>
                  </Link>
                  
                  {/* Sub-navigation tabs indented under the property */}
                  <ul className="space-y-0.5 pl-4 ml-2 border-l border-gray-700">
                    {/* Inspections Link - First as requested */}
                    <li>
                      <Link 
                        to={`/property/${propertyId}?tab=inspections`}
                        className={`flex items-center p-2 text-sm rounded-lg ${activeTab === 'inspections' || activeTab === '' ? 'bg-blue-700 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                      >
                        <FileText className="w-4 h-4" />
                        <span className="ml-3">Inspections</span>
                        {currentProperty.inspectionRecords && currentProperty.inspectionRecords.length > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 ml-auto text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                            {currentProperty.inspectionRecords.length}
                          </span>
                        )}
                      </Link>
                    </li>
                    
                    {/* Work Items Link */}
                    <li>
                      <Link 
                        to={`/property/${propertyId}?tab=workItems`}
                        className={`flex items-center p-2 text-sm rounded-lg ${activeTab === 'workItems' ? 'bg-blue-700 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                      >
                        <Hammer className="w-4 h-4" />
                        <span className="ml-3">Work Items</span>
                        {currentProperty.workItems && currentProperty.workItems.length > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 ml-auto text-xs font-medium text-green-800 bg-green-100 rounded-full">
                            {currentProperty.workItems.length}
                          </span>
                        )}
                      </Link>
                    </li>
                    
                    {/* Recommendations Link */}
                    <li>
                      <Link 
                        to={`/property/${propertyId}?tab=recommendations`}
                        className={`flex items-center p-2 text-sm rounded-lg ${activeTab === 'recommendations' ? 'bg-blue-700 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                      >
                        <Lightbulb className="w-4 h-4" />
                        <span className="ml-3">Recommendations</span>
                        {currentProperty.propertyAnalysis?.ImprovementPlan && currentProperty.propertyAnalysis.ImprovementPlan.length > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 ml-auto text-xs font-medium text-purple-800 bg-purple-100 rounded-full">
                            {currentProperty.propertyAnalysis.ImprovementPlan.length}
                          </span>
                        )}
                      </Link>
                    </li>
                  </ul>
                </div>
              ) : null
            ) : null}
            
            {/* Tools Section - Always shown */}
            <div className="pt-4 mt-4 border-t border-gray-700">
              <h3 className="px-2 text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">
                Tools
              </h3>
              
              <li>
                <Link 
                  to="/inspection" 
                  className={`flex items-center p-2 text-gray-300 rounded-lg ${isActive('/inspection') ? 'bg-gray-700' : 'hover:bg-gray-700'}`}
                >
                  <FileCheck className="w-5 h-5" />
                  <span className="ml-3">Inspection Tool</span>
                </Link>
              </li>
              
              <li>
                <Link 
                  to="/improvement" 
                  className={`flex items-center p-2 text-gray-300 rounded-lg ${isActive('/improvement') ? 'bg-gray-700' : 'hover:bg-gray-700'}`}
                >
                  <Lightbulb className="w-5 h-5" />
                  <span className="ml-3">Recommendation Wizard</span>
                </Link>
              </li>
              
              <li>
                <Link 
                  to="/quote" 
                  className={`flex items-center p-2 text-gray-300 rounded-lg ${isActive('/quote') ? 'bg-gray-700' : 'hover:bg-gray-700'}`}
                >
                  <BarChart3 className="w-5 h-5" />
                  <span className="ml-3">Quote Analyzer</span>
                </Link>
              </li>
            </div>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SideNavigation;